import React from 'react';
import moment from 'moment';

export const validatorConfig = {
  getConfig(t) {
    return {
      validators: {
        phone_number: {
          message: t('error_phone_number'),
          rule: val => {
            var formats = '(999)999-9999|(999) 999-9999';
            var r = RegExp('^(' + formats.replace(/([()])/g, '\\$1').replace(/9/g, '\\d') + ')$');
            return r.test(val);
          },
          messageReplace: (message, [format]) => message.replace(':format', format),
        },
        after_now: {
          message: t('error_date_should_be_after_now'),
          rule: val => {
            return moment(val).isAfter(moment());
          },
          messageReplace: (message, [after]) =>
            message.replace(':after', moment.unix(after).format('MMMM Do YYYY, h:mm:ss a')),
        },
        after: {
          message: t('error_date_should_be_after', { afterDate: ':after' }),
          rule: (val, [after]) => {
            return val.isAfter(moment.unix(after));
          },
          messageReplace: (message, [after]) =>
            message.replace(':after', moment.unix(after).format('MMMM Do YYYY, h:mm:ss a')),
        },
        date_format: {
          message: t('error_date_format', { format: t('date_format') }),
          rule: val => {
            return moment(val).isValid();
          },
        },
        length_greater_or_equal: {
          message: t('error_length_greater_or_equal', {
            minValue: ':minValue',
          }),
          rule: (val, [min]) => val.length >= Number(min),
          messageReplace: (message, [min]) => message.replace(':minValue', min),
        },
        length_less_or_equal: {
          message: t('error_length_less_or_equal', {
            maxValue: ':maxValue',
          }),
          rule: (val, [max]) => val.length <= Number(max),
          messageReplace: (message, [max]) => message.replace(':maxValue', max),
        },
        greater_than_or_equal: {
          message: t('error_greater_than_or_equal', {
            minValue: ':minValue',
            field: ':attribute',
          }),
          rule: (val, [min]) => val >= Number(min),
          messageReplace: (message, [min]) => message.replace(':minValue', min),
        },
        less_than_or_equal: {
          message: t('error_less_than_or_equal', {
            maxValue: ':maxValue',
            field: ':attribute',
          }),
          rule: (val, [max]) => val <= Number(max),
          messageReplace: (message, [max]) => message.replace(':maxValue', max),
        },
        postal_code: {
          message: t('error_postal_code'),
          rule: (val, params, validator) => {
            return validator.helpers.testRegex(val, /^[ABCEGHJ-NPRSTVXY]\d[ABCEGHJ-NPRSTV-Z][ -]?\d[ABCEGHJ-NPRSTV-Z]\d$/i);
          },
          required: true,
        },
        required_quill: {
          message: t('error_required_quill', {
            field: ':attribute',
          }),
          rule: val => {
            return val !== '' && val !== '<p><br></p>' && val !== '<p></p>' && val !== null && val !== undefined;
          },
          required: true,
        },
        exclusive_with: {
          message: t('error_exclusive_with', {
            field: ':attribute',
            exclusiveField: ':exclusiveField',
          }),
          rule: (val, params) => {
            return (
              (val !== undefined && (params[0] === 'undefined' || params[0] === 'null' || params[0] === '')) ||
              (val === undefined && (params[0] !== 'undefined' || params[0] !== 'null' || params[0] !== ''))
            );
          },
          messageReplace: (message, params) => message.replace(':exclusiveField', params[1]),
        },
        must_match: {
          message: t('error_must_match', {
            field: ':attribute',
            matchField: ':matchField',
          }),
          rule: (val, params) => {
            return val !== undefined && params[0] === params[1];
          },
          messageReplace: (message, params) => message.replace(':matchField', params[2]),
        },
        between_list: {
          message: t('error_between_list', {
            field: ':attribute',
            intervals: ':intervals',
          }),
          rule: function rule(val, params) {
            var isValid = true;

            if (params.length > 1) {
              for (let index = 0; index < params.length; index++) {
                const start = Number(params[index]);
                const end = Number(params[index + 1]);
                const value = Number(val);

                if (isValid) {
                  // Value should not be between the values
                  isValid = !(value >= start && value <= end);
                }
                index++;
              }
            }

            return isValid;
          },
          messageReplace: function messageReplace(message, params) {
            var intervals = '(';

            for (let index = 0; index < params.length; index++) {
              const start = params[index];
              const end = params[index + 1];

              intervals += `${index !== 0 ? ',' : ''}${start}/${end}`;

              index++;
            }

            intervals += ')';

            return message.replace(':intervals', intervals);
          },
        },
        health_insurance_number: {
          message: t('error_health_insurance_number'),
          rule: val => {
            var r = RegExp('^([A-Z]{4})+ ([0-9]{4})+ ([0-9]{4})+$');
            return r.test(val);
          },
          messageReplace: (message, [format]) => message.replace(':format', format),
        },
        social_insurance_number: {
          message: t('error_social_insurance_number'),
          rule: val => {
            var r = RegExp('^\\d{3}( ?)\\d{3}\\1\\d{3}$');

            return r.test(val);
          },
          messageReplace: (message, [format]) => message.replace(':format', format),
        },
        email: {
          message: t('error_email'),
          rule: val => {
            var r = RegExp('^\\S+@\\S+\\.\\S+$');
            return r.test(val) && val?.length > 0;
          },
          messageReplace: (message, [format]) => message.replace(':format', format),
        },
      },
      messages: {
        after_now: t('field_after_now'),
        after: t('error_date_should_be_after_now', { field: ':attribute' }),
        required: t('field_required', { field: ':attribute' }),
        date: t('error_date_format', { format: t('date_format') }),
        date_format: t('error_date_format', {
          format: t('date_format_with_time'),
        }),
        error_length_greater_or_equal: t('error_length_greater_or_equal', { min: ':minValue' }),
        error_length_less_or_equal: t('error_length_less_or_equal', { max: ':maxValue' }),
        error_greater_than_or_equal: t('error_greater_than_or_equal', { min: ':minValue' }),
        error_less_than_or_equal: t('error_less_than_or_equal', { max: ':maxValue' }),
        min: t('error_min_size', { field: ':attribute', length: ':min' }),
        not_in: t('error_unique', { field: ':attribute' }),
        url: t('error_url', { field: ':attribute' }),
      },
      element: message => <small className="form-control-feedback text-danger">{message}</small>,
    };
  },
};
